'use client';

import { createContext, useContext, useState, useEffect } from 'react';
import { usePathname } from 'next/navigation';
import { CUSTOMER_SPECIFIC_PATHS } from '@/constant/navigation';

const TabContext = createContext();

export const useTabContext = () => {
  const context = useContext(TabContext);
  if (!context) {
    // During SSR or initial hydration, return default values instead of throwing
    if (typeof window === 'undefined') {
      return { selectedTab: 'customer', setSelectedTab: () => {} };
    }
    throw new Error('useTabContext must be used within TabContextProvider');
  }
  return context;
};

const getTabFromPath = (pathname) => {
  // Check if it's a professional path
  if (pathname.startsWith('/professionals')) {
    return 'adviser';
  }

  // Check if it's a customer-specific path or root
  const isCustomerPath = CUSTOMER_SPECIFIC_PATHS.some(customerPath =>
    pathname === customerPath || pathname.startsWith(customerPath + '/')
  ) || pathname === '/';

  // Default to customer for customer paths and unknown paths
  return isCustomerPath ? 'customer' : 'customer';
};

const TabContextProvider = ({ children }) => {
  const pathname = usePathname();
  const pathBasedTab = getTabFromPath(pathname);

  // Always start with a path-based tab to ensure SSR/client consistency
  const [selectedTab, setSelectedTab] = useState(pathBasedTab);
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);

    // Always use path-based tab determination - no sessionStorage override for specific pages
    if (pathBasedTab === 'adviser') {
      // Always use adviser for /professionals paths
      setSelectedTab('adviser');
    } else {
      // For customer paths, always use customer tab
      setSelectedTab('customer');
    }
  }, [pathBasedTab]);

  useEffect(() => {
    if (isHydrated) {
      sessionStorage.setItem('selectedTab', selectedTab);
    }
  }, [selectedTab, isHydrated]);

  return (
    <TabContext.Provider value={{ selectedTab, setSelectedTab }}>
      {children}
    </TabContext.Provider>
  );
};

export default TabContextProvider;
